import {
	Clear as ClearIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	KeyboardArrowUp as KeyboardArrowUpIcon,
	OpenInNew as OpenInNewIcon
} from "@mui/icons-material";
import {
	Autocomplete,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid2,
	IconButton,
	InputAdornment,
	Switch,
	TextField,
	Typography,
	useTheme
} from "@mui/material";
import { TablePagination } from "../../components/common/pagination";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import ApplicationsSkeleton from "../../components/internal-user/applications/ApplicationsSkeleton";
import ErrorDisplay from "../../components/internal-user/applications/ErrorDisplay";
import FreelancerDetails from "../../components/common/FreelancerDetails";
import NoFreelancersFound from "../../components/internal-user/applications/NoFreelancersFound";
import { useInternalUserContext } from "../../contexts/useInternalUserContext";
import {
	useFreelancerCountryCounts,
	useFreelancersProfiles,
	useUpdateFreelancerProfileControlledStatus
} from "../../hooks/useFreelancerProfile";
import {
	useUpdateFreelancerOnboardingStatus,
	useUpdateFreelancerOnboardingExpeditedStatus
} from "../../hooks/useFreelancerOnboarding";
import { useExpertise } from "../../hooks/useReferential";
import { FreelancersProfilesSortField } from "../../models/enums/FreelancersProfilesSortField";
import { SortDirection } from "../../models/enums/SortDirection";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { FreelancerProfileSearchRequest } from "../../models/requests/FreelancerProfileSearchRequest";
import { formatDate } from "../../utils/formatting";
import { stringAvatar } from "../../utils/avatarUtils";
import { toOnboardingStatusName } from "../../utils/onboardingStatus";
import { CountryCount } from "../../models/CountryCount";
import { FreelancerProfile } from "../../models/FreelancerProfile";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";

const Applications: React.FC = () => {
	const {
		selectedFreelancerOnboardingStatus,
		setSelectedFreelancerOnboardingStatus,
		setIsUIDisabled,
		isUIDisabled
	} = useInternalUserContext();
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const [nameInputValue, setNameInputValue] = useState<string | null>(null);
	const [nameInputError, setNameInputError] = useState<string>("");
	const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());
	const [confirmationDialog, setConfirmationDialog] = useState<{
		open: boolean;
		type?: "status" | "expedited" | "controlled";
		freelancerOnboardingId?: number;
		freelancerProfileId?: number;
		currentStatus?: FreelancerOnboardingStatus;
		newStatus?: FreelancerOnboardingStatus;
		newExpeditedStatus?: boolean;
		newControlledStatus?: boolean;
		freelancerFullName?: string;
	}>({ open: false });

	// Local state for optimistic updates
	const [localStatusUpdates, setLocalStatusUpdates] = useState<
		Map<number, FreelancerOnboardingStatus>
	>(new Map());
	const [localExpeditedUpdates, setLocalExpeditedUpdates] = useState<
		Map<number, boolean>
	>(new Map());
	const [localControlledUpdates, setLocalControlledUpdates] = useState<
		Map<number, boolean>
	>(new Map());

	const [freelancerProfileSearchRequest, setFreelancerProfileSearchRequest] =
		useState<FreelancerProfileSearchRequest>({
			name: null,
			onboardingStatus:
				selectedFreelancerOnboardingStatus === "All"
					? null
					: selectedFreelancerOnboardingStatus,
			expertiseId: null,
			isOnboardingExpedited: null,
			wasItUpdated: null,
			country: null,
			languageCode: i18n.language,
			sort: {
				sortField: FreelancersProfilesSortField.CreatedAt,
				sortDirection: SortDirection.Desc
			},
			page: 1,
			pageSize: 10
		});
	const [totalPages, setTotalPages] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const { navigateByEnvironment } = useEnvironmentNavigation();

	const sortOptions = Object.values(FreelancersProfilesSortField).map(field => ({
		value: field,
		label: t(`enums.freelancersSortField.${field}`)
	}));

	const statusOptions = Object.values(FreelancerOnboardingStatus)
		.filter((value): value is FreelancerOnboardingStatus => typeof value === "number")
		.map((status, index) => ({
			order: index + 1,
			label: FreelancerOnboardingStatus[status],
			value: status
		}));
	const rowsPerPageOptions = [10, 25, 50];
	const { data, isLoading, error, refetch, isFetching } = useFreelancersProfiles(
		freelancerProfileSearchRequest
	);

	const { data: expertiseData, isLoading: expertiseLoading } = useExpertise();

	const { data: countryCountsData, isLoading: countryCountsLoading } =
		useFreelancerCountryCounts(toOnboardingStatusName(selectedFreelancerOnboardingStatus));

	const countryDisplayNames = useMemo(
		() => new Intl.DisplayNames([i18n.language], { type: "region" }),
		[i18n.language]
	);

	const getCountryOptionLabel = (option: CountryCount) =>
		`${countryDisplayNames.of(option.countryCode) || option.countryCode} (${option.count})`;

	const countryOptions = useMemo(() => {
		const options = [...(countryCountsData ?? [])];
		const selectedCountry = freelancerProfileSearchRequest.country;
		if (
			selectedCountry &&
			!options.some(option => option.countryCode === selectedCountry)
		) {
			options.push({ countryCode: selectedCountry, count: 0 });
		}
		return options.sort((a, b) =>
			(countryDisplayNames.of(a.countryCode) || a.countryCode).localeCompare(
				countryDisplayNames.of(b.countryCode) || b.countryCode,
				i18n.language
			)
		);
	}, [
		countryCountsData,
		freelancerProfileSearchRequest.country,
		countryDisplayNames,
		i18n.language
	]);

	const updateStatusMutation = useUpdateFreelancerOnboardingStatus();
	const updateExpeditedMutation = useUpdateFreelancerOnboardingExpeditedStatus();
	const updateControlledMutation = useUpdateFreelancerProfileControlledStatus();

	useEffect(() => {
		if (nameInputValue === null) return;

		const timeoutId = setTimeout(
			() => {
				const searchName = nameInputValue.trim();

				if (searchName.length > 0 && searchName.length <= 2) return;

				setFreelancerProfileSearchRequest(prev => ({
					...prev,
					name: searchName,
					page: 1
				}));
			},
			nameInputValue !== "" ? 1000 : 0
		);

		return () => clearTimeout(timeoutId);
	}, [nameInputValue]);

	useEffect(() => {
		setFreelancerProfileSearchRequest(prev => ({
			...prev,
			onboardingStatus:
				selectedFreelancerOnboardingStatus === "All"
					? null
					: selectedFreelancerOnboardingStatus,
			page: 1
		}));
	}, [selectedFreelancerOnboardingStatus]);

	useEffect(() => {
		if (data) {
			setTotalPages(data.totalPages || 0);
			setTotalCount(data.totalCount || 0);
		}
	}, [data]);

	// Clear local updates when data changes (page change, filters, etc.)
	useEffect(() => {
		setLocalStatusUpdates(new Map());
		setLocalExpeditedUpdates(new Map());
		setLocalControlledUpdates(new Map());
	}, [
		freelancerProfileSearchRequest.page,
		freelancerProfileSearchRequest.name,
		freelancerProfileSearchRequest.expertiseId,
		freelancerProfileSearchRequest.isOnboardingExpedited,
		freelancerProfileSearchRequest.wasItUpdated,
		freelancerProfileSearchRequest.country,
		freelancerProfileSearchRequest.onboardingStatus
	]);

	// Set UI disabled state based on loading/fetching
	useEffect(() => {
		setIsUIDisabled(isLoading || isFetching);
	}, [isLoading, isFetching, setIsUIDisabled]);

	const toggleFreelancerDetails = (freelancerProfileId: string) => {
		setExpandedPanels(prev => {
			const newSet = new Set(prev);
			if (newSet.has(freelancerProfileId)) {
				newSet.delete(freelancerProfileId);
			} else {
				newSet.add(freelancerProfileId);
			}
			return newSet;
		});
	};

	const handleNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (isUIDisabled) return;

		const newValue = event.target.value;
		const maxLength = 250;

		if (newValue.length > maxLength) {
			setNameInputError(
				t("pages.applications.filters.nameMaxLengthError", { maxLength })
			);
			return;
		}

		setNameInputError("");
		setNameInputValue(newValue);
	};

	const freelancersProfiles = data?.items || [];

	// Helper functions to get current values considering local updates
	const getCurrentStatus = (
		onboardingId: number,
		originalStatus: FreelancerOnboardingStatus
	): FreelancerOnboardingStatus => {
		return localStatusUpdates.get(onboardingId) ?? originalStatus;
	};

	const getCurrentExpeditedStatus = (
		onboardingId: number,
		originalExpedited: boolean
	): boolean => {
		return localExpeditedUpdates.get(onboardingId) ?? originalExpedited;
	};

	const getCurrentControlledStatus = (
		profileId: number,
		originalControlled: boolean
	): boolean => {
		return localControlledUpdates.get(profileId) ?? originalControlled;
	};

	// Helper function to get the current status option for Autocomplete
	const getCurrentStatusOption = (freelancerProfile: FreelancerProfile) => {
		const currentStatus = getCurrentStatus(
			freelancerProfile.onboarding.id,
			freelancerProfile.onboarding.currentStatus
		);
		return statusOptions.find(x => x.value === currentStatus) || undefined;
	};

	// Helper function to handle status option change
	const handleStatusOptionChange = (
		freelancerProfile: FreelancerProfile,
		newValue: FreelancerOnboardingStatus
	) => {
		if (newValue && freelancerProfile.onboarding?.id) {
			const currentStatus = getCurrentStatus(
				freelancerProfile.onboarding.id,
				freelancerProfile.onboarding.currentStatus
			);

			handleStatusChange(
				freelancerProfile.onboarding.id,
				currentStatus,
				newValue,
				freelancerProfile.freelancer?.user?.fullName!
			);
		}
	};

	// Helper function to handle expedited switch change
	const handleExpeditedSwitchChange = (
		freelancerProfile: FreelancerProfile,
		checked: boolean
	) => {
		if (freelancerProfile.onboarding?.id) {
			handleExpeditedChange(
				freelancerProfile.onboarding.id,
				checked,
				freelancerProfile.freelancer?.user?.fullName!
			);
		}
	};

	// Helper function to handle controlled switch change
	const handleControlledSwitchChange = (
		freelancerProfile: FreelancerProfile,
		checked: boolean
	) => {
		if (freelancerProfile.id) {
			handleControlledChange(
				freelancerProfile.id,
				checked,
				freelancerProfile.freelancer?.user?.fullName!
			);
		}
	};

	// Helper function to navigate to talent details
	const handleNavigateToTalent = (freelancerProfileKey: string) => {
		navigateByEnvironment(`/applications/talent/${freelancerProfileKey}`);
	};

	// Helper function to toggle freelancer details panel
	const handleToggleFreelancerDetails = (freelancerProfileId: string, index: number) => {
		const panelId = freelancerProfileId || `freelancer-profiles-${index}`;
		toggleFreelancerDetails(panelId);
	};

	// Helper function to close confirmation dialog
	const handleCloseConfirmationDialog = () => {
		setConfirmationDialog({ open: false });
	};

	// Helper function to clear name input
	const handleClearNameInput = () => {
		if (!isUIDisabled) {
			setNameInputValue("");
			setNameInputError("");
		}
	};

	// Helper function to clear all filters
	const handleClearFilters = () => {
		setSelectedFreelancerOnboardingStatus("All");
		setNameInputValue("");
		setNameInputError("");
		setFreelancerProfileSearchRequest(prev => ({
			...prev,
			name: null,
			expertiseId: null,
			isOnboardingExpedited: null,
			wasItUpdated: null,
			country: null,
			onboardingStatus: null,
			page: 1
		}));
	};

	// Helper function to render the cards list
	const renderCardsList = () => {
		if (isLoading || isFetching) {
			return <ApplicationsSkeleton />;
		}

		if (error) {
			return <ErrorDisplay onRetry={refetch} />;
		}

		return (
			<Box>
				{freelancersProfiles.length === 0 ? (
					<NoFreelancersFound onClearFilters={handleClearFilters} />
				) : (
					<Card
						sx={{
							borderBottomLeftRadius: 0,
							borderBottomRightRadius: 0
						}}
					>
						{freelancersProfiles.map((freelancerProfile, index) => (
							<CardContent
								key={freelancerProfile.id}
								sx={{
									pt: index === 0 ? "32px" : "auto"
								}}
							>
								{/* Top row with status indicator, avatar, info, and icons */}
								<Box display="flex" alignItems="flex-start" gap={3}>
									{/* Avatar */}
									<Avatar
										{...stringAvatar(
											freelancerProfile.freelancer?.user?.firstName ?? "",
											freelancerProfile.freelancer?.user?.lastName ?? ""
										)}
										src={`${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${freelancerProfile.freelancer?.picture?.urlPath}`}
									/>

									{/* Main content */}
									<Box flex={1}>
										{/* Name, Expertise, Experience */}
										<Box display="flex" flexDirection="row" alignItems="center" gap={1}>
											<Typography variant="h3" component="span">
												{freelancerProfile.freelancer?.user?.firstName}{" "}
												{freelancerProfile.freelancer?.user?.lastName}
											</Typography>
											{(freelancerProfile.freelancer?.user?.firstName ||
												!freelancerProfile.freelancer?.user?.lastName) &&
												(freelancerProfile.expertise?.label ||
													freelancerProfile.experience?.label) && (
													<Divider orientation="vertical" variant="middle" flexItem />
												)}
											<Typography variant="body1" component="span">
												{freelancerProfile?.expertise?.label &&
													t(`Expertises.${freelancerProfile.expertise?.label}`)}{" "}
												{freelancerProfile.expertise?.label &&
													freelancerProfile.experience?.label &&
													"- "}
												{freelancerProfile.experience?.label &&
													t(`Experiences.${freelancerProfile.experience?.label}`)}
											</Typography>
										</Box>

										{/* Dates */}
										<Box mt={"1px"}>
											<Typography variant="body2" fontWeight={500}>
												{t("pages.applications.card.registrationDate")} :{" "}
												<Typography variant="body2" component="span" lineHeight={1.9}>
													{formatDate(freelancerProfile.freelancer?.user?.createdAt)}
												</Typography>
											</Typography>
											<Typography variant="body2" fontWeight={500}>
												{" "}
												{t("pages.applications.card.updatedDate")} :{" "}
												<Typography variant="body2" component="span" lineHeight={1.9}>
													{freelancerProfile.updatedAt
														? `${formatDate(freelancerProfile.updatedAt.toString())} ${
																freelancerProfile.updatedBy
																	? `(${freelancerProfile.updatedBy.fullName})`
																	: ""
														  }`
														: "-"}
												</Typography>
											</Typography>
											<Typography variant="body2" fontWeight={500} display={"none"}>
												{" "}
												{t("pages.applications.card.lastContact")} :{" "}
												<Typography variant="body2" component="span" lineHeight={1.9}>
													{" "}
													-
												</Typography>
											</Typography>
											<Typography variant="body2" fontWeight={500} display={"none"}>
												{t("pages.applications.card.nextContact")} :{" "}
												<Typography variant="body2" component="span" lineHeight={1.9}>
													{" "}
													-
												</Typography>
											</Typography>
										</Box>
									</Box>

									{/* Right side with icons and status */}
									<Box
										display="flex"
										flexDirection="column"
										alignItems="flex-end"
										gap={4}
										position="relative"
										bottom={24}
									>
										{/* Action icons at the top */}
										<Box display="flex" gap={0.7}>
											<IconButton
												disableRipple
												onClick={() => handleNavigateToTalent(freelancerProfile.key)}
											>
												<OpenInNewIcon />
											</IconButton>
											<IconButton
												disableRipple
												onClick={() =>
													handleToggleFreelancerDetails(
														freelancerProfile.id?.toString() || "",
														index
													)
												}
											>
												{expandedPanels.has(
													freelancerProfile.id?.toString() ||
														`freelancer-profiles-${index}`
												) ? (
													<KeyboardArrowDownIcon />
												) : (
													<KeyboardArrowUpIcon />
												)}
											</IconButton>
										</Box>

										{/* Status dropdown and expedited switch below icons */}
										<Box display="flex" flexDirection="column" gap={1}>
											<Autocomplete
												size="small"
												sx={{ minWidth: 250 }}
												options={statusOptions}
												getOptionLabel={option =>
													`${option.order}. ${t(
														`enums.FreelancerOnboardingStatus.${option.label}`
													)}`
												}
												value={getCurrentStatusOption(freelancerProfile)}
												onChange={(_, newValue) =>
													handleStatusOptionChange(freelancerProfile, newValue.value)
												}
												disabled={
													isUIDisabled ||
													getCurrentExpeditedStatus(
														freelancerProfile.onboarding.id,
														freelancerProfile.onboarding.isOnboardingCurrentlyExpedited ||
															false
													) === true
												}
												disableClearable
												renderInput={params => (
													<TextField
														{...params}
														variant="outlined"
														sx={{
															"& .MuiOutlinedInput-root": {
																py: 0.5
															}
														}}
													/>
												)}
											/>

											<FormControlLabel
												control={
													<Switch
														checked={getCurrentExpeditedStatus(
															freelancerProfile.onboarding.id,
															freelancerProfile.onboarding
																.isOnboardingCurrentlyExpedited || false
														)}
														onChange={event =>
															handleExpeditedSwitchChange(
																freelancerProfile,
																event.target.checked
															)
														}
														disabled={isUIDisabled}
														size="small"
														color="secondary"
													/>
												}
												label={t("pages.applications.card.expedited")}
												sx={{
													ml: 0,
													"& .MuiFormControlLabel-label": {
														fontSize: "0.875rem"
													}
												}}
											/>

											<FormControlLabel
												control={
													<Switch
														checked={getCurrentControlledStatus(
															freelancerProfile.id,
															freelancerProfile.isControlled || false
														)}
														onChange={event =>
															handleControlledSwitchChange(
																freelancerProfile,
																event.target.checked
															)
														}
														disabled={isUIDisabled}
														size="small"
														color="secondary"
													/>
												}
												label={t("pages.applications.card.controlled")}
												sx={{
													ml: 0,
													"& .MuiFormControlLabel-label": {
														fontSize: "0.875rem"
													}
												}}
											/>
										</Box>
									</Box>
								</Box>

								{/* Freelancer Details Panel */}
								<Collapse
									in={expandedPanels.has(
										freelancerProfile.id?.toString() || `freelancer-profiles-${index}`
									)}
									timeout={400}
									unmountOnExit
								>
									<FreelancerDetails
										freelancerProfileId={freelancerProfile.id}
										isExpanded={expandedPanels.has(
											freelancerProfile.id?.toString() || `freelancer-profiles-${index}`
										)}
										key={`freelancer-details-${freelancerProfile.id}`}
									/>
								</Collapse>

								{index < freelancersProfiles.length - 1 && (
									<Divider
										sx={{ height: 16 }}
										orientation="horizontal"
										variant="middle"
									/>
								)}
							</CardContent>
						))}
					</Card>
				)}
			</Box>
		);
	};

	// Handle status change
	const handleStatusChange = (
		freelancerOnboardingId: number,
		currentStatus: FreelancerOnboardingStatus,
		newStatus: FreelancerOnboardingStatus,
		freelancerFullName: string
	) => {
		// Always show confirmation dialog for status changes
		setConfirmationDialog({
			open: true,
			type: "status",
			freelancerOnboardingId,
			currentStatus,
			newStatus,
			freelancerFullName
		});
	};

	// Handle confirmation dialog
	const handleConfirmStatusChange = async () => {
		if (confirmationDialog.type === "status") {
			// Handle status change confirmation
			if (
				confirmationDialog.freelancerOnboardingId &&
				confirmationDialog.newStatus !== undefined
			) {
				// Optimistic update - update local state immediately
				setLocalStatusUpdates(
					prev =>
						new Map(
							prev.set(
								confirmationDialog.freelancerOnboardingId!,
								confirmationDialog.newStatus!
							)
						)
				);

				try {
					await updateStatusMutation.mutateAsync({
						freelancerOnboardingId: confirmationDialog.freelancerOnboardingId,
						newStatus: confirmationDialog.newStatus
					});
					// Success - the optimistic update is confirmed, no need to do anything
				} catch (error) {
					console.error("Error updating freelancer onboarding status:", error);
					// Revert optimistic update on error
					setLocalStatusUpdates(prev => {
						const newMap = new Map(prev);
						newMap.delete(confirmationDialog.freelancerOnboardingId!);
						return newMap;
					});
				}
			}
		} else if (confirmationDialog.type === "expedited") {
			// Handle expedited status confirmation
			if (
				confirmationDialog.freelancerOnboardingId &&
				confirmationDialog.newExpeditedStatus !== undefined
			) {
				// Optimistic update - update local state immediately
				setLocalExpeditedUpdates(
					prev =>
						new Map(
							prev.set(
								confirmationDialog.freelancerOnboardingId!,
								confirmationDialog.newExpeditedStatus!
							)
						)
				);

				try {
					await updateExpeditedMutation.mutateAsync({
						freelancerOnboardingId: confirmationDialog.freelancerOnboardingId,
						newIsOnboardingCurrentlyExpedited: confirmationDialog.newExpeditedStatus
					});
					// Success - the optimistic update is confirmed, no need to do anything
				} catch (error) {
					console.error("Error updating freelancer onboarding expedited status:", error);
					// Revert optimistic update on error
					setLocalExpeditedUpdates(prev => {
						const newMap = new Map(prev);
						newMap.delete(confirmationDialog.freelancerOnboardingId!);
						return newMap;
					});
				}
			}
		} else if (confirmationDialog.type === "controlled") {
			// Handle controlled status confirmation
			if (
				confirmationDialog.freelancerProfileId &&
				confirmationDialog.newControlledStatus !== undefined
			) {
				setLocalControlledUpdates(
					prev =>
						new Map(
							prev.set(
								confirmationDialog.freelancerProfileId!,
								confirmationDialog.newControlledStatus!
							)
						)
				);

				try {
					await updateControlledMutation.mutateAsync({
						freelancerProfileId: confirmationDialog.freelancerProfileId,
						newIsControlled: confirmationDialog.newControlledStatus
					});
				} catch (error) {
					console.error("Error updating freelancer profile controlled status:", error);
					setLocalControlledUpdates(prev => {
						const newMap = new Map(prev);
						newMap.delete(confirmationDialog.freelancerProfileId!);
						return newMap;
					});
				}
			}
		}
		setConfirmationDialog({ open: false });
	};

	// Handle expedited status change
	const handleExpeditedChange = (
		freelancerOnboardingId: number,
		newExpeditedStatus: boolean,
		freelancerFullName: string
	) => {
		// Always show confirmation dialog for expedited status changes
		setConfirmationDialog({
			open: true,
			type: "expedited",
			freelancerOnboardingId,
			newExpeditedStatus,
			freelancerFullName
		});
	};

	// Handle controlled status change
	const handleControlledChange = (
		freelancerProfileId: number,
		newControlledStatus: boolean,
		freelancerFullName: string
	) => {
		// Always show confirmation dialog for controlled status changes
		setConfirmationDialog({
			open: true,
			type: "controlled",
			freelancerProfileId,
			newControlledStatus,
			freelancerFullName
		});
	};

	return (
		<>
			<Box px={{ xs: 2, sm: 4, md: 6 }} pt={3} minHeight={"calc(100% - 70px)"}>
				{/* Header */}
				<Box mb={3}>
					<Typography variant="h2">
						{t("pages.applications.title", {
							status:
								selectedFreelancerOnboardingStatus === "All"
									? t("menu.applicationAll")
									: t(
											`enums.FreelancerOnboardingStatus.${selectedFreelancerOnboardingStatus}`
									  )
						})}
					</Typography>
				</Box>

				{/* Filters */}
				<Grid2 container spacing={2} mb={3}>
					<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
						<TextField
							label={t("pages.applications.filters.nameLabel")}
							value={nameInputValue ?? ""}
							onChange={handleNameInputChange}
							size="medium"
							error={!!nameInputError}
							helperText={nameInputError}
							fullWidth
							sx={{
								...(isUIDisabled && {
									"& .MuiInputBase-root": {
										color: theme.palette.text.disabled,
										cursor: "default"
									},
									"& .MuiInputLabel-root": {
										color: theme.palette.text.disabled
									},
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: theme.palette.action.disabled
									},
									"& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: theme.palette.action.disabled
									}
								})
							}}
							variant="outlined"
							placeholder={t("pages.applications.filters.namePlaceholder")}
							slotProps={{
								input: {
									readOnly: isUIDisabled,
									endAdornment: nameInputValue && (
										<InputAdornment position="end">
											<IconButton
												aria-label="clear search"
												onClick={handleClearNameInput}
												edge="end"
												size="small"
												disabled={isUIDisabled}
											>
												<ClearIcon />
											</IconButton>
										</InputAdornment>
									)
								}
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
						<Autocomplete
							size="medium"
							fullWidth
							options={expertiseData || []}
							groupBy={option => option.category}
							getOptionLabel={option => t(`Expertises.${option.label}`)}
							value={
								freelancerProfileSearchRequest.expertiseId
									? (expertiseData ?? []).find(
											exp => exp.id === freelancerProfileSearchRequest.expertiseId
									  )
									: null
							}
							onChange={(_, newValue) =>
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									page: 1,
									expertiseId: newValue?.id
								}))
							}
							loading={expertiseLoading}
							disabled={isUIDisabled}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							renderInput={params => (
								<TextField
									{...params}
									label={t("pages.applications.filters.expertiseLabel")}
									placeholder={t("pages.applications.filters.expertisePlaceholder")}
									disabled={isUIDisabled}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
						<Autocomplete
							size="medium"
							fullWidth
							options={[
								{ value: true, label: t("common.yes") },
								{ value: false, label: t("common.no") }
							]}
							getOptionLabel={option => option.label}
							value={
								freelancerProfileSearchRequest.isOnboardingExpedited === null
									? null
									: {
											value: freelancerProfileSearchRequest.isOnboardingExpedited,
											label: freelancerProfileSearchRequest.isOnboardingExpedited
												? t("common.yes")
												: t("common.no")
									  }
							}
							onChange={(_, newValue) =>
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									page: 1,
									isOnboardingExpedited: newValue?.value ?? null
								}))
							}
							disabled={isUIDisabled}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							renderInput={params => (
								<TextField
									{...params}
									label={t("pages.applications.filters.expeditedLabel")}
									placeholder={t("pages.applications.filters.expeditedPlaceholder")}
									disabled={isUIDisabled}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
						<Autocomplete
							size="medium"
							fullWidth
							options={[
								{ value: true, label: t("common.yes") },
								{ value: false, label: t("common.no") }
							]}
							getOptionLabel={option => option.label}
							value={
								freelancerProfileSearchRequest.wasItUpdated === null
									? null
									: {
											value: freelancerProfileSearchRequest.wasItUpdated,
											label: freelancerProfileSearchRequest.wasItUpdated
												? t("common.yes")
												: t("common.no")
									  }
							}
							onChange={(_, newValue) =>
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									page: 1,
									wasItUpdated: newValue?.value ?? null
								}))
							}
							disabled={isUIDisabled}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							renderInput={params => (
								<TextField
									{...params}
									label={t("pages.applications.filters.hasUpdateDateLabel")}
									placeholder={t("pages.applications.filters.hasUpdateDatePlaceholder")}
									disabled={isUIDisabled}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
						<Autocomplete
							size="medium"
							fullWidth
							options={countryOptions}
							getOptionLabel={getCountryOptionLabel}
							value={
								freelancerProfileSearchRequest.country
									? countryOptions.find(
											option =>
												option.countryCode === freelancerProfileSearchRequest.country
									  ) ?? null
									: null
							}
							onChange={(_, newValue) =>
								setFreelancerProfileSearchRequest(prev => ({
									...prev,
									page: 1,
									country: newValue?.countryCode ?? null
								}))
							}
							loading={countryCountsLoading}
							disabled={isUIDisabled}
							isOptionEqualToValue={(option, value) =>
								option.countryCode === value.countryCode
							}
							renderInput={params => (
								<TextField
									{...params}
									label={t("pages.applications.filters.countryLabel")}
									placeholder={t("pages.applications.filters.countryPlaceholder")}
									disabled={isUIDisabled}
								/>
							)}
						/>
					</Grid2>
				</Grid2>

				{/* Cards List */}
				{renderCardsList()}
			</Box>

			{/* Status Change Confirmation Dialog */}
			<Dialog
				open={confirmationDialog.open}
				onClose={handleCloseConfirmationDialog}
				aria-labelledby="status-change-dialog-title"
				aria-describedby="status-change-dialog-description"
			>
				<DialogTitle id="status-change-dialog-title">
					{confirmationDialog.type === "expedited"
						? t("pages.applications.confirmExpeditedChange.title")
						: confirmationDialog.type === "controlled"
							? t("pages.applications.confirmControlledChange.title")
							: t("pages.applications.confirmStatusChange.title")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="status-change-dialog-description">
						{confirmationDialog.type === "expedited" ? (
							<Trans
								i18nKey={"pages.applications.confirmExpeditedChange.message"}
								components={{
									strong: <strong />
								}}
								values={{
									freelancerName: confirmationDialog.freelancerFullName
								}}
							/>
						) : confirmationDialog.type === "controlled" ? (
							<Trans
								i18nKey={"pages.applications.confirmControlledChange.message"}
								components={{
									strong: <strong />
								}}
								values={{
									freelancerName: confirmationDialog.freelancerFullName
								}}
							/>
						) : (
							<Trans
								i18nKey={"pages.applications.confirmStatusChange.message"}
								components={{
									strong: <strong />
								}}
								values={{
									freelancerName: confirmationDialog.freelancerFullName,
									currentStatus: t(
										`enums.FreelancerOnboardingStatus.${
											FreelancerOnboardingStatus[confirmationDialog.currentStatus!]
										}`
									),
									newStatus: t(
										`enums.FreelancerOnboardingStatus.${
											FreelancerOnboardingStatus[confirmationDialog.newStatus!]
										}`
									)
								}}
							/>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						disableRipple
						color="secondary"
						onClick={handleCloseConfirmationDialog}
						disabled={updateStatusMutation.isPending || updateExpeditedMutation.isPending || updateControlledMutation.isPending}
					>
						{t("common.cancel")}
					</Button>
					<Button
						disableRipple
						color="secondary"
						onClick={handleConfirmStatusChange}
						variant="contained"
						disabled={updateStatusMutation.isPending || updateExpeditedMutation.isPending || updateControlledMutation.isPending}
					>
						{t("common.confirm")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Unified Pagination Component */}
			<TablePagination
				currentPage={freelancerProfileSearchRequest.page}
				pageSize={freelancerProfileSearchRequest.pageSize}
				totalCount={totalCount || 0}
				totalPages={totalPages}
				onPageChange={page => {
					setFreelancerProfileSearchRequest(prev => ({ ...prev, page }));
				}}
				onPageSizeChange={pageSize => {
					setFreelancerProfileSearchRequest(prev => ({
						...prev,
						pageSize,
						page: 1
					}));
				}}
				pageSizeOptions={rowsPerPageOptions}
				sortOptions={sortOptions}
				currentSort={{
					sortField: freelancerProfileSearchRequest.sort?.sortField || undefined,
					sortDirection: freelancerProfileSearchRequest.sort?.sortDirection as
						| "Asc"
						| "Desc"
						| undefined
				}}
				onSortChange={sort => {
					setFreelancerProfileSearchRequest(prev => ({
						...prev,
						page: 1,
						sort: sort.sortField
							? {
									sortField: sort.sortField as FreelancersProfilesSortField,
									sortDirection: (sort.sortDirection || "Asc") as SortDirection
							  }
							: null
					}));
				}}
				disabled={isUIDisabled}
				sticky={true}
			/>
		</>
	);
};

export default Applications;
